import React, { useState, useMemo } from 'react';
import { Modal, Button, ButtonGroup } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';
import dayjs from 'dayjs';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import styles from './styles.module.scss';

interface TemplateChartModalProps {
  isOpen: boolean;
  onClose: () => void;
  template: {
    id: string;
    name: string;
    format: string;
    totalUses: number;
    lastUsedAt: string | null;
    usageTrend: Array<{ date: string; count: number }>;
  };
}

type GroupBy = 'daily' | 'weekly' | 'monthly';

const TemplateChartModal: React.FC<TemplateChartModalProps> = ({ isOpen, onClose, template }) => {
  const [groupBy, setGroupBy] = useState<GroupBy>('daily');

  const aggregatedData = useMemo(() => {
    if (!template.usageTrend) return [];

    switch (groupBy) {
      case 'weekly': {
        const weeklyData: { [key: string]: number } = {};
        
        template.usageTrend.forEach(({ date, count }) => {
          const weekStart = dayjs(date).startOf('week').format('YYYY-MM-DD');
          weeklyData[weekStart] = (weeklyData[weekStart] || 0) + count;
        });
        
        return Object.entries(weeklyData)
          .map(([date, count]) => ({ date, count }))
          .sort((a, b) => a.date.localeCompare(b.date));
      }
      
      case 'monthly': {
        const monthlyData: { [key: string]: number } = {};
        
        template.usageTrend.forEach(({ date, count }) => {
          const monthStart = dayjs(date).startOf('month').format('YYYY-MM-DD');
          monthlyData[monthStart] = (monthlyData[monthStart] || 0) + count;
        });
        
        return Object.entries(monthlyData)
          .map(([date, count]) => ({ date, count }))
          .sort((a, b) => a.date.localeCompare(b.date));
      }
      
      case 'daily':
      default:
        return template.usageTrend;
    }
  }, [template.usageTrend, groupBy]);

  const formatDateAxis = (date: string): string => {
    switch (groupBy) {
      case 'weekly':
        return dayjs(date).format('MMM D');
      case 'monthly':
        return dayjs(date).format('MMM YYYY');
      case 'daily':
      default:
        return dayjs(date).format('MMM D');
    }
  };

  const formatTooltipDate = (date: string): string => {
    switch (groupBy) {
      case 'weekly':
        return `Week of ${dayjs(date).format('MMM D, YYYY')}`;
      case 'monthly':
        return dayjs(date).format('MMMM YYYY');
      case 'daily':
      default:
        return dayjs(date).format('MMMM D, YYYY');
    }
  };

  const maxUsage = Math.max(...aggregatedData.map(d => d.count), 1);
  const yAxisDomain = [0, Math.ceil(maxUsage * 1.1)]; // Add 10% padding to top

  return (
    <Modal 
      show={isOpen} 
      onHide={onClose} 
      size="lg" 
      className={styles.chartModal}
      style={{ zIndex: 10000 }}
      backdrop="static"
    >
      <Modal.Header closeButton>
        <Modal.Title>{template.name} - Usage Analytics</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className={styles.chartControls}>
          <ButtonGroup>
            <Button
              variant={groupBy === 'daily' ? 'primary' : 'outline-primary'}
              size="sm"
              onClick={() => setGroupBy('daily')}
              style={{ marginRight: '4px' }}
            >
              <FormattedMessage id="label.daily" defaultMessage="Daily" />
            </Button>
            <Button
              variant={groupBy === 'weekly' ? 'primary' : 'outline-primary'}
              size="sm"
              onClick={() => setGroupBy('weekly')}
              style={{ marginRight: '4px' }}
            >
              <FormattedMessage id="label.weekly" defaultMessage="Weekly" />
            </Button>
            <Button
              variant={groupBy === 'monthly' ? 'primary' : 'outline-primary'}
              size="sm"
              onClick={() => setGroupBy('monthly')}
            >
              <FormattedMessage id="label.monthly" defaultMessage="Monthly" />
            </Button>
          </ButtonGroup>
        </div>

        <div className={styles.chartContainer}>
          {aggregatedData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart
                data={aggregatedData}
                margin={{ top: 10, right: 20, left: 10, bottom: 50 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis
                  dataKey="date"
                  tickFormatter={formatDateAxis}
                  angle={-45}
                  textAnchor="end"
                  tick={{ fontSize: 12 }}
                />
                <YAxis
                  domain={yAxisDomain}
                  tick={{ fontSize: 12 }}
                  label={{
                    value: 'Number of Uses',
                    angle: -90,
                    position: 'insideLeft',
                    style: { fontSize: 14 },
                  }}
                />
                <Tooltip
                  labelFormatter={formatTooltipDate}
                  formatter={(value: any) => [`${value} uses`, 'Usage']}
                  contentStyle={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                  }}
                />
                <Legend
                  verticalAlign="top"
                  height={36}
                  content={() => (
                    <div className={styles.legendContent}>
                      <span className={styles.legendItem}>
                        <span className={styles.legendDot} />
                        Template Usage
                      </span>
                      <span className={styles.legendStats}>
                        Total: {template.totalUses} uses
                      </span>
                    </div>
                  )}
                />
                <Line
                  type="monotone"
                  dataKey="count"
                  stroke="#007bff"
                  strokeWidth={2}
                  dot={{ fill: '#007bff', r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className={styles.noDataMessage}>
              <FormattedMessage
                id="message.noUsageData"
                defaultMessage="No usage data available for this template"
              />
            </div>
          )}
        </div>

        <div className={styles.chartFooter}>
          <small>
            <FormattedMessage
              id="message.chartInteraction"
              defaultMessage="Hover over chart for details"
            />
          </small>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default TemplateChartModal;